import { labelingToState } from '../model'
import { LabelingDto, LabelingFormState } from '../types'

export type CopySourceProduct = { productId: string; title: string; nr: string | null }

export type CopySourceCategory = { categoryId: string; title: string; products: CopySourceProduct[] }

export type CopySourceNode = { key: string; title: string; count: number; products: CopySourceProduct[]; children: CopySourceNode[] }

export type StoreCategory = { id: string; title: string; parent?: { id: string; title: string } | null }

type RawChild = { key: string; title: string; products: CopySourceProduct[] }

type RawTop = { key: string; title: string; products: CopySourceProduct[]; children: Map<string, RawChild> }

const matches = (text: string, query: string): boolean => text.toLowerCase().includes(query)

const sumCounts = (products: CopySourceProduct[], children: CopySourceNode[]): number =>
	products.length + children.reduce((sum, child) => sum + child.count, 0)

const toChildNode = (child: RawChild): CopySourceNode =>
	({ key: child.key, title: child.title, count: child.products.length, products: child.products, children: [] })

const toTopNode = (top: RawTop): CopySourceNode => {
	const children = Array.from(top.children.values()).map(toChildNode).filter((child) => child.count > 0)
	return { key: top.key, title: top.title, count: sumCounts(top.products, children), products: top.products, children }
}

const groupByTopCategory = (categories: CopySourceCategory[], storeCategories: StoreCategory[]): RawTop[] => {
	const storeById = new Map(storeCategories.map((category) => [category.id, category]))
	const tops = new Map<string, RawTop>()

	categories.forEach((category) => {
		const storeCategory = storeById.get(category.categoryId)
		if (storeCategory?.parent) {
			if (!tops.has(storeCategory.parent.id)) tops.set(storeCategory.parent.id, { key: storeCategory.parent.id, title: storeCategory.parent.title, products: [], children: new Map() })
			const top = tops.get(storeCategory.parent.id) as RawTop
			if (!top.children.has(category.categoryId)) top.children.set(category.categoryId, { key: category.categoryId, title: storeCategory.title, products: [] })
			const child = top.children.get(category.categoryId) as RawChild
			child.products.push(...category.products)
		} else {
			if (!tops.has(category.categoryId)) tops.set(category.categoryId, { key: category.categoryId, title: storeCategory?.title ?? category.title, products: [], children: new Map() })
			const top = tops.get(category.categoryId) as RawTop
			top.products.push(...category.products)
		}
	})

	return Array.from(tops.values())
}

export const buildCopySourceTree = (categories: CopySourceCategory[], storeCategories: StoreCategory[]): CopySourceNode[] =>
	groupByTopCategory(categories, storeCategories)
		.map(toTopNode)
		.filter((node) => node.count > 0)

const filterProducts = (products: CopySourceProduct[], query: string): CopySourceProduct[] =>
	products.filter((product) => matches(product.title, query) || (product.nr != null && matches(product.nr, query)))

const filterChildNode = (child: CopySourceNode, query: string): CopySourceNode => {
	if (matches(child.title, query)) return child
	const products = filterProducts(child.products, query)
	return { ...child, count: products.length, products }
}

const filterTopNode = (top: CopySourceNode, query: string): CopySourceNode | null => {
	if (matches(top.title, query)) return top
	const children = top.children.map((child) => filterChildNode(child, query)).filter((child) => child.count > 0)
	const products = filterProducts(top.products, query)
	const count = sumCounts(products, children)
	return count > 0 ? { ...top, count, products, children } : null
}

export const filterCopySourceTree = (nodes: CopySourceNode[], query: string): CopySourceNode[] => {
	const normalizedQuery = query.trim().toLowerCase()
	if (!normalizedQuery) return nodes
	return nodes
		.map((node) => filterTopNode(node, normalizedQuery))
		.filter((node): node is CopySourceNode => node !== null)
}

export const applyCopiedLabeling = (current: LabelingFormState, source: LabelingDto | null): LabelingFormState => {
	const next = labelingToState(source)
	if (!next.selected.includes('l:HALAL')) return { ...next, halalCertifiedBy: current.halalCertifiedBy }
	return next
}
